import { useState, useCallback, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { isUnauthorizedError } from "@/lib/authUtils";
import { apiRequest } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileUpload } from "@/components/FileUpload";
import { ProcessingStatus } from "@/components/ProcessingStatus";
import { AppShell } from "@/components/app";
import { EmptyState } from "@/components/app";
import { Survey, ProcessingJob } from "@/types/survey";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Edit, 
  Trash2, 
  Plus, 
  Wand2, 
  FileText, 
  Grid3X3, 
  List, 
  Search,
  Filter,
  Upload,
  Users,
  Link2,
  Check,
  ExternalLink,
  PieChart,
  ChevronUp,
  FolderOpen,
  Loader2,
  Calendar
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ViewMode = "grid" | "table";
type StatusFilter = "all" | "draft" | "published" | "archived" | "ready";

// Pure utility functions - defined at file scope for stable identity
function getStatusColor(status: string): string {
  switch (status) {
    case "published":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
    case "draft":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
    case "archived":
      return "bg-muted text-muted-foreground";
    default:
      return "bg-muted text-muted-foreground";
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays < 7) return `${diffInDays}d ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w ago`;
  return date.toLocaleDateString();
}

// Props interface for extracted view components
interface ProjectViewProps {
  readyToCreateJobs: ProcessingJob[];
  filteredSurveys: Survey[];
  onCreateSurvey: (jobId: number) => void;
  creatingJobId: number | null;
  onCopyShareLink: (survey: Survey) => void;
  copiedSurveyId: number | null;
  onRequestDeleteSurvey: (survey: Survey) => void;
}

// Grid view component - defined outside parent for stable identity
function ProjectGridView({
  readyToCreateJobs,
  filteredSurveys,
  onCreateSurvey,
  creatingJobId,
  onCopyShareLink,
  copiedSurveyId,
  onRequestDeleteSurvey,
}: ProjectViewProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {/* Processing jobs ready to create */}
      {readyToCreateJobs.map((job: ProcessingJob) => (
        <Card key={`job-${job.id}`} className="group hover:shadow-lg hover:border-primary/20 transition-all duration-200">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Wand2 className="h-4 w-4 text-orange-600 flex-shrink-0" />
                  <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                    {job.fileName.replace(/\.[^/.]+$/, "")}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  {job.result?.questions?.length || 0} questions extracted
                </p>
              </div>
              <Badge className="bg-accent/10 text-accent border-0 flex-shrink-0">
                Ready
              </Badge>
            </div>
            
            {/* Stats row */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(job.createdAt)}</span>
              </div>
              <div className="text-sm text-muted-foreground uppercase">
                {job.fileType.includes('word') ? 'DOCX' : job.fileType.includes('pdf') ? 'PDF' : job.fileType.includes('excel') ? 'XLSX' : 'FILE'}
              </div>
            </div>
            
            {/* Actions */}
            <div className="mt-4">
              <Button 
                onClick={() => onCreateSurvey(job.id)}
                disabled={creatingJobId === job.id}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                {creatingJobId === job.id ? "Creating..." : "Create Survey"}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Existing surveys */}
      {filteredSurveys.map((survey: Survey) => (
        <Card key={`survey-${survey.id}`} className="group hover:shadow-lg hover:border-primary/20 transition-all duration-200">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <Link to={`/survey/${survey.id}`}>
                  <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors cursor-pointer">
                    {survey.title}
                  </h3>
                </Link>
                <p className="text-sm text-muted-foreground mt-1">
                  {survey.questions?.length || 0} questions
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Badge className={getStatusColor(survey.status)}>
                  {survey.status.charAt(0).toUpperCase() + survey.status.slice(1)}
                </Badge>
                {survey.settings?.quotasEnabled && (
                  <Badge variant="outline" className="gap-1">
                    <Users className="h-3 w-3" />
                    Quotas
                  </Badge>
                )}
              </div>
            </div>
            
            {survey.description && (
              <p className="text-sm text-muted-foreground/70 mt-2 line-clamp-2">
                {survey.description}
              </p>
            )}
            
            {/* Stats row */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Users className="h-4 w-4" />
                <span>{survey.responseCount ?? 0} responses</span>
              </div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(survey.updatedAt)}</span>
              </div>
            </div>
            
            {/* Actions - visible on hover */}
            <div className="flex items-center gap-1 mt-4 pt-4 border-t border-border opacity-0 group-hover:opacity-100 transition-opacity">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9"
                aria-label={`Edit ${survey.title}`}
                asChild
              >
                <Link to={`/survey/${survey.id}`}>
                  <Edit className="h-4 w-4" />
                </Link>
              </Button>
              {survey.status === "published" && survey.shareToken && (
                <>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-9 w-9"
                    aria-label="Preview live survey"
                    asChild
                  >
                    <a href={`/s/${survey.shareToken}`} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => onCopyShareLink(survey)}
                    aria-label="Copy survey link"
                  >
                    {copiedSurveyId === survey.id ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Link2 className="h-4 w-4" />
                    )}
                  </Button>
                </>
              )}
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9"
                aria-label="View responses"
                asChild
              >
                <Link to={`/survey/${survey.id}/responses`}>
                  <Users className="h-4 w-4" />
                </Link>
              </Button>
              {survey.settings?.quotasEnabled && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-9 w-9"
                  aria-label="View quotas"
                  asChild
                >
                  <Link to={`/survey/${survey.id}/quotas`}>
                    <PieChart className="h-4 w-4" />
                  </Link>
                </Button>
              )}
              {/* Spacer */}
              <div className="flex-1" />
              <Button 
                variant="ghost" 
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-destructive"
                onClick={() => onRequestDeleteSurvey(survey)}
                aria-label={`Delete ${survey.title}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Table view component - defined outside parent for stable identity
function ProjectTableView({
  readyToCreateJobs,
  filteredSurveys,
  onCreateSurvey,
  creatingJobId,
  onCopyShareLink,
  copiedSurveyId,
  onRequestDeleteSurvey,
}: ProjectViewProps) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full divide-y divide-border" style={{ minWidth: '800px' }}>
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Questions</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Created</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider" style={{ width: '220px', minWidth: '220px' }}>Actions</th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {readyToCreateJobs.map((job: ProcessingJob) => (
              <tr key={`job-${job.id}`} className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Wand2 className="h-4 w-4 text-accent" />
                    </div>
                    <div className="min-w-0 max-w-md">
                      <div className="text-sm font-medium text-foreground truncate">{job.fileName}</div>
                      <div className="text-xs text-muted-foreground">Processing complete</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Badge className="bg-accent/10 text-accent border-0">Ready</Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{job.result?.questions?.length || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatDate(job.createdAt)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ width: '120px', minWidth: '120px' }}>
                  <Button 
                    size="sm"
                    onClick={() => onCreateSurvey(job.id)}
                    disabled={creatingJobId === job.id}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {creatingJobId === job.id ? "Creating..." : "Create"}
                  </Button>
                </td>
              </tr>
            ))}
            {filteredSurveys.map((survey: Survey) => (
              <tr key={`survey-${survey.id}`} className="group hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4">
                  <Link to={`/survey/${survey.id}`}>
                    <div className="flex items-center gap-3 cursor-pointer">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-4 w-4 text-primary" />
                      </div>
                      <div className="min-w-0 max-w-md">
                        <div className="text-sm font-medium text-foreground group-hover:text-primary transition-colors truncate">{survey.title}</div>
                        {survey.description && (
                          <div className="text-xs text-muted-foreground truncate">{survey.description}</div>
                        )}
                      </div>
                    </div>
                  </Link>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Badge className={getStatusColor(survey.status)}>
                    {survey.status.charAt(0).toUpperCase() + survey.status.slice(1)}
                  </Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{survey.questions?.length || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatDate(survey.createdAt)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ width: '220px', minWidth: '220px' }}>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-9 w-9"
                      aria-label={`Edit ${survey.title}`}
                      asChild
                    >
                      <Link to={`/survey/${survey.id}`}>
                        <Edit className="h-4 w-4" />
                      </Link>
                    </Button>
                    {survey.status === "published" && survey.shareToken && (
                      <>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-9 w-9"
                          aria-label="Preview live survey"
                          asChild
                        >
                          <a href={`/s/${survey.shareToken}`} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => onCopyShareLink(survey)}
                          aria-label="Copy survey link"
                        >
                          {copiedSurveyId === survey.id ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Link2 className="h-4 w-4" />
                          )}
                        </Button>
                      </>
                    )}
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-9 w-9"
                      aria-label="View responses"
                      asChild
                    >
                      <Link to={`/survey/${survey.id}/responses`}>
                        <Users className="h-4 w-4" />
                      </Link>
                    </Button>
                    {survey.settings?.quotasEnabled && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-9 w-9"
                        aria-label="View quotas"
                        asChild
                      >
                        <Link to={`/survey/${survey.id}/quotas`}>
                          <PieChart className="h-4 w-4" />
                        </Link>
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-9 w-9 text-muted-foreground hover:text-destructive"
                      onClick={() => onRequestDeleteSurvey(survey)}
                      aria-label={`Delete ${survey.title}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Projects() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [creatingJobId, setCreatingJobId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [processingJobId, setProcessingJobId] = useState<number | null>(null);
  const [showUploadCard, setShowUploadCard] = useState(false);
  const [copiedSurveyId, setCopiedSurveyId] = useState<number | null>(null);
  const [surveyToDelete, setSurveyToDelete] = useState<Survey | null>(null);
  
  // Ref for copy timeout cleanup on unmount
  const copyTimeoutRef = useRef<number | null>(null);
  
  // Cleanup timeout on unmount to prevent setState on unmounted component
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    };
  }, []);

  // All hooks must be called before any conditional returns
  const { data: surveys = [], isLoading: surveysLoading } = useQuery({
    queryKey: ["/api/surveys"],
    retry: false,
  });

  const { data: processingJobs = [], isLoading: jobsLoading } = useQuery({
    queryKey: ["/api/processing"],
    retry: false,
  });

  // Destructure mutate for stable reference (Pattern A)
  const { mutate: createSurvey } = useMutation({
    mutationFn: async (jobId: number) => {
      setCreatingJobId(jobId);
      const response = await apiRequest("POST", `/api/surveys/from-job/${jobId}`, {});
      return response.json();
    },
    onSuccess: () => {
      setCreatingJobId(null);
      toast({
        title: "Survey Created",
        description: "Your survey has been successfully created and is ready to edit.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/surveys"] });
      queryClient.invalidateQueries({ queryKey: ["/api/processing"] });
    },
    onError: (error) => {
      setCreatingJobId(null);
      if (isUnauthorizedError(error as Error)) {
        toast({
          title: "Unauthorized",
          description: "Please log in to continue.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }
      toast({
        title: "Error",
        description: "Failed to create survey from processing job",
        variant: "destructive",
      });
    },
  });

  const deleteSurveyMutation = useMutation({
    mutationFn: async (surveyId: number) => {
      await apiRequest("DELETE", `/api/surveys/${surveyId}`);
    },
    onSuccess: () => {
      toast({
        title: "Survey Deleted",
        description: "Survey has been successfully deleted.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/surveys"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete survey",
        variant: "destructive",
      });
    },
  });

  // Stable callback handlers for child components
  const handleCreateSurvey = useCallback((jobId: number) => {
    createSurvey(jobId);
  }, [createSurvey]);

  const handleCopyShareLink = useCallback(async (survey: Survey) => {
    if (survey.shareToken) {
      const shareUrl = `${window.location.origin}/s/${survey.shareToken}`;
      
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopiedSurveyId(survey.id);
        
        // Clear any existing timeout and set a new one
        if (copyTimeoutRef.current) {
          window.clearTimeout(copyTimeoutRef.current);
          copyTimeoutRef.current = null;
        }
        copyTimeoutRef.current = window.setTimeout(() => setCopiedSurveyId(null), 2000);
        
        toast({
          title: "Link Copied!",
          description: "Share this link with your respondents.",
        });
      } catch {
        // Clipboard API failed (permissions, non-HTTPS, iframe restrictions, etc.)
        toast({
          title: "Copy Failed",
          description: "Please copy the link manually from the address bar.",
          variant: "destructive",
        });
      }
    }
  }, [toast]);

  // Rename for clarity: this opens the delete modal, doesn't delete immediately
  const handleRequestDeleteSurvey = useCallback((survey: Survey) => {
    setSurveyToDelete(survey);
  }, []);

  // Early return after all hooks
  if (!user) {
    return null;
  }

  // Filter and search logic
  const filteredSurveys = (surveys as Survey[]).filter(survey => {
    const matchesSearch = survey.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || survey.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const readyToCreateJobs = (processingJobs as ProcessingJob[]).filter(job => 
    job.status === "completed" && 
    (statusFilter === "all" || statusFilter === "ready")
  );

  // Props for view components - using satisfies for type validation
  const viewProps = {
    readyToCreateJobs,
    filteredSurveys,
    onCreateSurvey: handleCreateSurvey,
    creatingJobId,
    onCopyShareLink: handleCopyShareLink,
    copiedSurveyId,
    onRequestDeleteSurvey: handleRequestDeleteSurvey,
  } satisfies ProjectViewProps;

  return (
    <AppShell
      title="Projects"
      subtitle="Manage your surveys and responses"
      maxWidth="wide"
      actions={
        <Button
          variant="outline"
          onClick={() => setShowUploadCard(!showUploadCard)}
        >
          {showUploadCard ? (
            <>
              <ChevronUp className="h-4 w-4 mr-2" />
              Hide Upload
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Upload Document
            </>
          )}
        </Button>
      }
    >
      {/* Upload Section */}
      <AnimatePresence>
        {showUploadCard && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="mb-8 border-2 border-dashed border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                    <Upload className="h-8 w-8 text-primary animate-pulse" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Transform Your Documents
                  </h3>
                  <p className="text-muted-foreground">
                    Upload PDFs, Word docs, Excel files, or images to create professional digital surveys
                  </p>
                </div>
                
                <div className="max-w-2xl mx-auto">
                  <FileUpload onProcessingComplete={(jobId) => {
                    setProcessingJobId(jobId);
                    setShowUploadCard(false);
                  }} />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Processing Status */}
      {processingJobId && (
        <div className="mb-8">
          <ProcessingStatus
            jobId={processingJobId}
            onComplete={() => {
              setProcessingJobId(null);
              queryClient.invalidateQueries({ queryKey: ["/api/surveys"] });
              queryClient.invalidateQueries({ queryKey: ["/api/processing"] });
            }}
            onCancel={() => setProcessingJobId(null)}
          />
        </div>
      )}

      {/* Filter/Search Bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search surveys..." 
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        {/* Filter Controls */}
        <div className="flex gap-2">
          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="w-[140px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="ready">Ready</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          
          {/* View Toggle */}
          <div className="flex items-center border rounded-lg p-1 bg-muted/50">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-3"
              onClick={() => setViewMode("grid")}
            >
              <Grid3X3 className="h-4 w-4 mr-2" />
              Grid
            </Button>
            <Button
              variant={viewMode === "table" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-3"
              onClick={() => setViewMode("table")}
            >
              <List className="h-4 w-4 mr-2" />
              List
            </Button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      {surveysLoading || jobsLoading ? (
        // Loading state
        <div className="text-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading projects...</p>
        </div>
      ) : filteredSurveys.length === 0 && readyToCreateJobs.length === 0 ? (
        // Empty state
        <EmptyState
          icon={FolderOpen}
          title="No projects found"
          description={searchQuery || statusFilter !== "all" 
            ? "Try adjusting your search or filters" 
            : "Upload a document to create your first survey"}
          action={!searchQuery && statusFilter === "all" ? {
            label: "Upload Document",
            onClick: () => setShowUploadCard(true),
            icon: Upload
          } : undefined}
        />
      ) : (
        // Content
        viewMode === "grid" ? <ProjectGridView {...viewProps} /> : <ProjectTableView {...viewProps} />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!surveyToDelete} onOpenChange={() => setSurveyToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Survey</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "<span className="font-semibold">{surveyToDelete?.title}</span>"? 
              This action cannot be undone and all responses will be permanently lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (surveyToDelete) {
                  deleteSurveyMutation.mutate(surveyToDelete.id);
                  setSurveyToDelete(null);
                }
              }}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {deleteSurveyMutation.isPending ? "Deleting..." : "Delete Survey"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
